import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { Location, NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@/environments/environment';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { CreateBriefService } from '../briefs/service/create-brief/create-brief.service';
import { BriefService } from '../../services/brief/brief.service';
import { AlertService } from '../../atoms/alert/alert.service';
import { BriefResponseDto } from '../../common/general-types/briefs.dto';
import { UgcProductResponseDto } from '../../common/general-types/products.dto';
import { CampaignDto } from '../../common/general-types/campaigns.dto';
import { BasePricingModel } from '../../common/general-types/pricing.dto';
import { CreateMonitoringBriefDto, CreateSocialScreeningBriefDto, MonitorTypeEnum } from '../../common/general-types/social-screening.dto';
import { BRIEF_BUILDER_GOALS } from '../create-brief-select/brief-builder.constants';
import { LoadingOverlayProgressComponent } from '../../atoms/loading-overlay-progress/loading-overlay-progress.component';
import { ProductSection } from '../create-brief-select/sections/product-section/product-section';
import { ContentSection } from '../create-brief-select/sections/content-section/content-section';
import { AudienceSection } from '../create-brief-select/sections/audience-section/audience-section';
import { FOLLOWER_RANGES } from '../create-brief-select/brief-builder.constants';
import { createDefaultPieceEditor, editorFromSavedPieces, PieceEditorState, piecesFromEditor } from '@/app/common/brief-pieces/piece-list';
import { SmartBriefExtractService, BriefFileTextResponse, GoogleDocTextResponse } from './smart-brief-extract.service';
import { SmartBriefDraftService } from './smart-brief-draft.service';
import { smartBriefDraftWrite } from './smart-brief-draft';
import {
  applyExtractedBrief,
  canCreateBrief,
  composerIntent,
  currentExtractedBrief,
  ExtractedBrief,
  mergeExtractedBrief,
  pieceSummary,
} from './smart-brief-state';
import { Piece } from '@/app/common/brief-pieces/piece-list';
import { CreateBriefSelectSmartBriefType } from './drawers/a-brief-type/a-brief-type';
import { AddBriefChoice, CreateBriefSelectSmartAddBrief } from './drawers/a-add-brief/a-add-brief';
import { CreateBriefSelectSmartProduct } from './drawers/a-product/a-product';
import { LiveBrief } from './molecules/live-brief/live-brief';
import { ScreeningWindow } from '../screening-brief-select/sections/screening-window/screening-window';
import { isPastCalendarDate, toScreenUntilInstant } from '../screening-brief-select/sections/screening-window/screening-window.helpers';
import { CreateScreeningService } from '../screening-brief-create/services/create-screening/create-screening.service';
import { SocialScreeningService } from '../../services/social-screening/social-screening.service';
import { MonitoringRange, MonitoringWindow } from '../monitoring/molecules/create-monitoring-brief/sections/monitoring-window/monitoring-window';
import { CreateMonitoring } from '../monitoring/molecules/create-monitoring-brief/services/create-monitoring';
import { fromIsoDate, isValidMonitoringEnd, isValidMonitoringStart } from '../monitoring/molecules/create-monitoring-brief/sections/monitoring-window/monitoring-window.helpers';
import { SocialMonitoring } from '../../services/social-monitoring/social-monitoring';
import { S3uploaderService } from '../../services/s3uploader/s3uploader.service';
import { ImageService } from '../../services/image/image.service';
import { productPriceLabel } from './drawers/a-product/a-product.helpers';
import { S3BucketEnum } from '../../common/general-types/media-assets.dto';
import {
  AI_HELP_QUESTIONS,
  ageBracketsFor,
  answeredQuestions,
  answerAiHelp,
  answerMandatoryItems,
  answerRestrictions,
  askForMissingCopy,
  applyFactRead,
  askForMonitoring,
  askForPieces,
  askForReview,
  askForScreening,
  BriefType,
  canCreate,
  chooseBriefType,
  chooseCreatorCount,
  chooseProduct,
  reaskProduct,
  chooseReport,
  type ChosenProductSnapshot,
  confirmAudience,
  describeChange,
  confirmPieces,
  creatorCountLabel,
  currentAiHelpQuestion,
  liveBriefValue,
  looksGood,
  loneBriefFileLink,
  loneGoogleDocLink,
  nextQuestion,
  plainTextParagraph,
  openSmartBrief,
  SmartBriefAudience,
  SmartBriefAudienceSelection,
  SmartBriefConversation,
  SmartBriefDraft,
  setLookback,
  setMonitoringWindow,
  setReportPrompt,
  startAiHelp,
  startOver as resetConversation,
  submitSuppliedFile,
  submitSuppliedGoogleDoc,
  submitTypedBrief,
} from './molecules/conversation/smart-brief-conversation';
import { openingThread, OpeningMessage } from './molecules/conversation/smart-brief-thread';

type Step = 'source' | 'product' | 'pieces' | 'creators' | 'ready';
type ReadState = 'idle' | 'reading' | 'read' | 'error';

type ChatMessage = {
  id: number;
  role: 'guide' | 'user';
  text: string;
};

@Component({
  selector: 'app-create-brief-select-smart',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, ContentSection, AudienceSection, CreateBriefSelectSmartBriefType, CreateBriefSelectSmartAddBrief, CreateBriefSelectSmartProduct, LiveBrief, ScreeningWindow, MonitoringWindow],
  templateUrl: './create-brief-select-smart.html',
  styleUrl: './create-brief-select-smart.scss',
})
export class CreateBriefSelectSmart implements OnInit, OnDestroy {
  private readonly briefFileInput = viewChild<ElementRef<HTMLInputElement>>('briefFile');
  private readonly briefComposer = viewChild<ElementRef<HTMLTextAreaElement>>('briefComposer');
  private readonly thread = viewChild<ElementRef<HTMLElement>>('thread');
  private greetingTimer: ReturnType<typeof setTimeout> | null = null;
  private followUpTimer: ReturnType<typeof setTimeout> | null = null;
  private scrollTimer: ReturnType<typeof setTimeout> | null = null;
  readonly createBriefService = inject(CreateBriefService);
  private readonly extractService = inject(SmartBriefExtractService);
  private readonly drafts = inject(SmartBriefDraftService);
  private readonly draftChanges = new Subject<void>();
  private acceptDraftSaves = true;
  private draftGeneration = 0;
  private readonly uploader = inject(S3uploaderService);
  private readonly briefService = inject(BriefService);
  private readonly alertService = inject(AlertService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly currentUser = inject(CurrentUserService);
  private readonly imageService = inject(ImageService);
  private readonly socialScreening = inject(SocialScreeningService);
  private readonly socialMonitoring = inject(SocialMonitoring);
  private readonly createScreeningService = inject(CreateScreeningService);
  private readonly createMonitoringService = inject(CreateMonitoring);
  private readonly subscriptionList = new Subscription();
  private readGeneration = 0;
  private pieceEditorSeeded = false;
  private screeningWindowSeeded = false;
  private classicScreenUntil: Date | undefined;
  private monitoringWindowSeeded = false;
  private classicMonitorStart: Date | undefined;
  private classicMonitorEnd: Date | undefined;
  private classicPieces: ReturnType<CreateBriefService['pieces']> | null = null;
  private classicEditor: PieceEditorState | null = null;
  private ugcHostSeeded = false;
  private classicCampaign: CampaignDto | undefined;
  private classicModel: BasePricingModel | undefined;
  private classicTitle = '';
  private classicPublic = true;
  private productSeeded = false;
  private classicProduct: UgcProductResponseDto | undefined;
  private classicAudience: {
    genders: number[];
    interests: number[];
    location: string[];
    age: { id: number; label: string; minAge: number; maxAge: number }[];
    countries: string[];
    is18plus: boolean;
    followerRanges: { min: number; max: number }[];
    ugcOnly: boolean;
    persona: NonNullable<ReturnType<CreateBriefService['creatorProfileModel']>> | undefined;
  } | null = null;

  readonly step = signal<Step>('source');
  readonly readState = signal<ReadState>('idle');
  readonly readError = signal('');
  readonly messages = signal<ChatMessage[]>([]);
  readonly draftText = signal('');
  readonly creatorOptions = [1, 5, 10, 25, 50];
  readonly creatorCountLabel = creatorCountLabel;
  /** Questions already answered, kept in the chat as bubbles (F7). */
  readonly answered = computed(() => answeredQuestions(this.conversation()));
  readonly pieceSummary = pieceSummary;
  readonly conversation = signal(openSmartBrief());
  readonly creating = signal(false);
  readonly createReady = computed(() => {
    const conversation = this.conversation();
    if (!canCreate(conversation) || this.creating()) {
      return false;
    }
    if (conversation.draft.briefType === 'ugc' && !this.createBriefService.hasSelectedPricingModel()) {
      return false;
    }
    return true;
  });
  readonly sentText = signal('');
  readonly holdMessage = signal('');
  readonly draftSaved = signal(false);
  readonly draftNotice = signal('');
  readonly savedCampaignId = signal<string | null>(null);
  readonly savedPricingModelId = signal<string | null>(null);
  readonly productCreate = signal(false);
  readonly showDraftSaved = computed(() => this.draftSaved() && this.conversation().draft.briefType !== null);
  readonly greetingReady = signal(false);
  readonly followUpReady = signal(false);
  private readonly openingPending = signal(true);

  private readonly watchDraft = effect(() => {
    this.conversation();
    this.draftText();
    this.createBriefService.selectedCampaign();
    this.createBriefService.selectedPricingModel();
    this.draftChanges.next();
  });

  private readonly watchThread = effect(() => {
    this.greetingReady();
    this.followUpReady();
    this.conversation();
    this.readState();
    this.sentText();
    this.scrollThread();
  });

  ngOnInit(): void {
    this.initFlow();
    this.subscriptionList.add(
      this.draftChanges.pipe(debounceTime(400)).subscribe(() => this.persistDraft()),
    );
    this.loadDraft();
  }

  ngOnDestroy(): void {
    this.clearOpeningTimers();
    this.restoreClassicDraft();
    this.subscriptionList.unsubscribe();
  }

  initFlow(): void {
    this.readGeneration += 1;
    this.conversation.set(openSmartBrief());
    this.step.set('source');
    this.readState.set('idle');
    this.readError.set('');
    this.messages.set([]);
    this.draftText.set('');
    this.sentText.set('');
    this.holdMessage.set('');
  }

  private beginGreeting(): void {
    this.clearOpeningTimers();
    this.openingPending.set(false);
    this.greetingReady.set(false);
    this.followUpReady.set(false);
    this.greetingTimer = setTimeout(() => {
      this.greetingTimer = null;
      this.greetingReady.set(true);
    }, 500);
  }

  private revealOpening(): void {
    this.clearOpeningTimers();
    this.openingPending.set(false);
    this.greetingReady.set(true);
    this.followUpReady.set(true);
  }

  private clearOpeningTimers(): void {
    if (this.greetingTimer !== null) {
      clearTimeout(this.greetingTimer);
      this.greetingTimer = null;
    }
    if (this.followUpTimer !== null) {
      clearTimeout(this.followUpTimer);
      this.followUpTimer = null;
    }
    if (this.scrollTimer !== null) {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = null;
    }
  }

  private scrollThread(): void {
    const element = this.thread()?.nativeElement;
    if (!element) {
      return;
    }
    element.scrollTop = element.scrollHeight;
    if (this.scrollTimer !== null) {
      clearTimeout(this.scrollTimer);
    }
    this.scrollTimer = setTimeout(() => {
      this.scrollTimer = null;
      element.scrollTop = element.scrollHeight;
    }, 50);
  }

  private loadDraft(): void {
    const businessAccountId = this.currentUser.focussedBrand?.id;
    if (!businessAccountId) {
      this.beginGreeting();
      this.applyRequestedBriefType();
      return;
    }
    this.subscriptionList.add(
      this.drafts.load(businessAccountId).subscribe({
        next: (record) => {
          if (!record?.conversation?.draft?.briefType) {
            this.beginGreeting();
            this.applyRequestedBriefType();
            return;
          }
          this.revealOpening();
          if (record.conversation.draft.briefType === 'ugc') {
            this.rememberUgcHost();
          }
          this.savedCampaignId.set(record.campaignId);
          this.savedPricingModelId.set(record.pricingModelId);
          this.conversation.set({
            ...record.conversation,
            replacedProducts: record.conversation.replacedProducts ?? [],
            copyReplies: record.conversation.copyReplies ?? { dos: '', donts: '' },
          });
          this.draftText.set(record.composerText ?? '');
          this.applyAudienceSelection(record.conversation.draft.audienceSelection);
          if (record.conversation.draft.briefType === 'ugc' && record.conversation.draft.pieces.length > 0) {
            this.seedPieceEditor(record.conversation.draft.pieces);
          }
          this.draftSaved.set(true);
          this.draftNotice.set('');
        },
        error: () => {
          this.draftSaved.set(false);
          this.draftNotice.set('The draft could not be saved.');
          this.beginGreeting();
        },
      }),
    );
  }

  private persistDraft(): void {
    if (!this.acceptDraftSaves) {
      return;
    }
    const businessAccountId = this.currentUser.focussedBrand?.id;
    if (!businessAccountId) {
      return;
    }
    const body = smartBriefDraftWrite({
      businessAccountId,
      conversation: this.conversation(),
      composerText: this.draftText(),
      campaignId: this.createBriefService.selectedCampaign()?.id ?? null,
      pricingModelId: this.createBriefService.selectedPricingModel()?.id ?? null,
      audienceSelection: this.audienceSelection(),
    });
    if (!body) {
      return;
    }
    const generation = this.draftGeneration + 1;
    this.draftGeneration = generation;
    this.subscriptionList.add(
      this.drafts.save(body).subscribe({
        next: () => {
          if (generation !== this.draftGeneration) {
            return;
          }
          this.draftSaved.set(true);
          this.draftNotice.set('');
        },
        error: () => {
          if (generation !== this.draftGeneration) {
            return;
          }
          this.draftSaved.set(false);
          this.draftNotice.set('The draft could not be saved.');
        },
      }),
    );
  }

  private abandonDraft(): void {
    const businessAccountId = this.currentUser.focussedBrand?.id;
    if (!businessAccountId) {
      this.acceptDraftSaves = true;
      return;
    }
    this.subscriptionList.add(
      this.drafts.abandon(businessAccountId).subscribe({
        next: () => {
          this.acceptDraftSaves = true;
          this.draftNotice.set('');
          this.persistDraft();
        },
        error: () => {
          this.acceptDraftSaves = true;
          this.draftNotice.set('The draft could not be saved.');
        },
      }),
    );
  }

  private completeDraft(briefId: string): void {
    const businessAccountId = this.currentUser.focussedBrand?.id;
    if (!businessAccountId || !briefId) {
      return;
    }
    this.subscriptionList.add(
      this.drafts.complete(businessAccountId, briefId).subscribe({
        next: () => this.draftNotice.set(''),
        error: () => this.draftNotice.set('The draft could not be saved.'),
      }),
    );
  }

  private audienceSelection(): SmartBriefAudienceSelection | null {
    if (this.conversation().draft.briefType !== 'ugc') {
      return null;
    }
    const persona = this.createBriefService.creatorProfileModel();
    return {
      genders: [...this.createBriefService.selectedGenders()],
      interests: [...this.createBriefService.selectedInterests()],
      countries: [...this.createBriefService.selectedCountryCodes()],
      is18plus: this.createBriefService.is18plus(),
      followerRanges: this.createBriefService.selectedFollowerRanges().map((range) => ({ ...range })),
      ages: this.createBriefService.selectedAge().map((age) => ({ ...age })),
      persona: persona ? { ...persona } : null,
    };
  }

  private applyAudienceSelection(selection: SmartBriefAudienceSelection | null | undefined): void {
    if (!selection) {
      return;
    }
    if (!this.classicAudience) {
      const persona = this.createBriefService.creatorProfileModel();
      this.classicAudience = {
        genders: [...this.createBriefService.selectedGenders()],
        interests: [...this.createBriefService.selectedInterests()],
        location: [...this.createBriefService.selectedLocation()],
        age: this.createBriefService.selectedAge().map((age) => ({ ...age })),
        countries: [...this.createBriefService.selectedCountryCodes()],
        is18plus: this.createBriefService.is18plus(),
        followerRanges: this.createBriefService.selectedFollowerRanges().map((range) => ({ ...range })),
        ugcOnly: this.createBriefService.selectedUgcOnly(),
        persona: persona ? { ...persona } : undefined,
      };
    }
    this.createBriefService.selectedGenders.set([...selection.genders]);
    this.createBriefService.selectedInterests.set([...selection.interests]);
    this.createBriefService.selectedCountryCodes.set([...selection.countries]);
    this.createBriefService.is18plus.set(selection.is18plus);
    this.createBriefService.selectedFollowerRanges.set(selection.followerRanges.map((range) => ({ ...range })));
    this.createBriefService.selectedAge.set(selection.ages.map((age) => ({ ...age })));
    this.createBriefService.creatorProfileModel.set(selection.persona ? { ...selection.persona } : undefined);
  }

  onDraftInput(event: Event): void {
    const target = event.target;
    if (!(target instanceof HTMLTextAreaElement)) {
      return;
    }
    this.draftText.set(target.value);
    this.holdMessage.set('');
  }

  canSend(): boolean {
    const step = this.conversation().step;
    if (step === 'report-prompt') {
      return this.conversation().draft.reportPrompt.length === 0;
    }
    return step === 'supply' || step === 'ai-help' || step === 'dos' || step === 'donts' || step === 'creators' || step === 'review';
  }

  activeQuestion(): string | null {
    return nextQuestion(this.conversation());
  }

  helpQuestion(briefType: BriefType, index: number): string {
    return AI_HELP_QUESTIONS[briefType][index] ?? '';
  }

  currentQuestion(): string | null {
    return currentAiHelpQuestion(this.conversation());
  }

  beginAiHelp(): void {
    this.conversation.update((current) => startAiHelp(current));
    this.draftText.set('');
    this.holdMessage.set('');
  }

  chooseSupply(choice: AddBriefChoice): void {
    if (this.conversation().step !== 'supply') {
      return;
    }
    if (choice === 'help') {
      this.beginAiHelp();
      return;
    }
    if (choice === 'upload') {
      this.briefFileInput()?.nativeElement.click();
      return;
    }
    this.briefComposer()?.nativeElement.focus();
  }

  composerPlaceholder(): string {
    if (this.conversation().step === 'intro' && this.greetingReady()) {
      return 'Pick one, or describe what you need…';
    }
    if (this.conversation().step === 'supply') {
      return 'Type your brief, paste a Google Doc link, or drop a file...';
    }
    if (this.conversation().step === 'dos') {
      return 'Type the mandatory items...';
    }
    if (this.conversation().step === 'donts') {
      return 'Type the restrictions...';
    }
    return '';
  }

  productImageUrl(image: string | null): string {
    return this.imageService.getImageSource(image, 'gallery');
  }

  productPrice(product: ChosenProductSnapshot): string {
    return productPriceLabel(product.value ?? undefined, product.currency ?? undefined);
  }

  showProductQuestion(): boolean {
    const current = this.conversation();
    return current.step === 'product' || !!current.draft.productId || current.replacedProducts.length > 0;
  }

  firstProductCard(): ChosenProductSnapshot | null {
    const current = this.conversation();
    if (current.replacedProducts.length > 0) {
      return current.replacedProducts[0];
    }
    return this.currentProductCard();
  }

  laterProductCards(): ChosenProductSnapshot[] {
    const current = this.conversation();
    if (current.replacedProducts.length === 0) {
      return [];
    }
    const rest = current.replacedProducts.slice(1);
    const currentCard = this.currentProductCard();
    return currentCard ? [...rest, currentCard] : rest;
  }

  showFreshProductQuestion(): boolean {
    const current = this.conversation();
    return current.step === 'product' && !current.draft.productId && current.replacedProducts.length > 0;
  }

  private currentProductCard(): ChosenProductSnapshot | null {
    const draft = this.conversation().draft;
    if (!draft.productId) {
      return null;
    }
    return {
      name: draft.productName ?? '',
      image: draft.productImage,
      description: draft.productDescription,
      value: draft.productValue,
      currency: draft.productCurrency,
    };
  }

  givenName(): string {
    const name = this.currentUser.shoutUser?.firstName;
    return typeof name === 'string' ? name : '';
  }

  openingMessages(): OpeningMessage[] {
    return openingThread(this.conversation(), this.givenName()).filter((message) => {
      if (message.kind === 'greeting') {
        return this.greetingReady();
      }
      if (message.kind === 'follow-up') {
        return this.followUpReady();
      }
      return true;
    });
  }

  showOpeningTyping(): boolean {
    if (this.openingPending()) {
      return false;
    }
    if (this.conversation().draft.briefType === null) {
      return !this.greetingReady();
    }
    return !this.followUpReady();
  }

  sendTypedBrief(): void {
    const step = this.conversation().step;
    if (step === 'dos' || step === 'donts') {
      this.sendCopy(step);
      return;
    }
    if (this.conversation().step === 'creators') {
      this.sendCreatorCount();
      return;
    }
    if (this.conversation().step === 'report-prompt') {
      this.sendReportPrompt();
      return;
    }
    if (this.conversation().step === 'review') {
      this.sendReviewChange();
      return;
    }
    if (this.conversation().step === 'supply') {
      const link = loneBriefFileLink(this.draftText());
      if (link) {
        this.readLinkedFile(link);
        return;
      }
      const googleDoc = loneGoogleDocLink(this.draftText());
      if (googleDoc) {
        this.readGoogleDoc(googleDoc.url);
        return;
      }
    }
    const result = this.conversation().step === 'ai-help'
      ? answerAiHelp(this.conversation(), this.draftText())
      : submitTypedBrief(this.conversation(), this.draftText());
    this.conversation.set(result.conversation);
    if (!result.read) {
      if (this.conversation().step === 'ai-help' && this.draftText().trim().length > 0) {
        this.draftText.set('');
      } else if (this.draftText().trim().length > 0) {
        this.holdMessage.set('Add a bit more so the brief can be read.');
      }
      return;
    }

    this.sentText.set(result.read.text);
    this.draftText.set('');
    this.holdMessage.set('');
    this.readFact(result.read.text, result.read.briefType);
  }

  openBriefFile(input: HTMLInputElement): void {
    if (this.conversation().step !== 'supply') {
      return;
    }
    input.click();
  }

  onBriefFile(event: Event): void {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) {
      return;
    }
    const file = input.files?.item(0) ?? null;
    input.value = '';
    this.acceptBriefFile(file);
  }

  onBriefDragOver(event: DragEvent): void {
    if (this.conversation().step !== 'supply') {
      return;
    }
    event.preventDefault();
  }

  onBriefDrop(event: DragEvent): void {
    if (this.conversation().step !== 'supply') {
      return;
    }
    event.preventDefault();
    this.acceptBriefFile(event.dataTransfer?.files.item(0) ?? null);
  }

  private acceptBriefFile(file: File | null): void {
    if (!file || this.conversation().step !== 'supply') {
      return;
    }
    const lower = file.name.toLowerCase();
    const allowed = lower.endsWith('.pdf') || lower.endsWith('.docx') || lower.endsWith('.txt');
    if (!allowed || file.size > 10 * 1024 * 1024) {
      this.holdMessage.set('This file could not be read.');
      return;
    }
    void this.uploadBriefFile(file);
  }

  private async uploadBriefFile(file: File): Promise<void> {
    const generation = this.readGeneration;
    this.holdMessage.set('');
    this.readState.set('reading');
    const selected = { length: 1, item: () => file };
    let uploaded: { fullUrl: string } | null = null;
    try {
      uploaded = await this.uploader.uploadDocument(selected, S3BucketEnum.CREATORS_CSV);
    } catch {
      uploaded = null;
    }
    if (generation !== this.readGeneration) {
      return;
    }
    const documentUrl = uploaded?.fullUrl ?? '';
    const fileKey = documentKeyFromUrl(documentUrl);
    if (!documentUrl || !fileKey) {
      this.readState.set('idle');
      this.holdMessage.set('This file could not be read.');
      return;
    }
    this.subscriptionList.add(
      this.extractService.readFile({ fileKey, fileName: file.name }).subscribe({
        next: (result) => {
          if (generation !== this.readGeneration) {
            return;
          }
          this.finishSuppliedFile(result, { fileName: file.name, documentUrl });
        },
        error: () => {
          if (generation !== this.readGeneration) {
            return;
          }
          this.readState.set('idle');
          this.keepFailedFile(file.name, documentUrl);
          this.holdMessage.set('This file could not be read.');
        },
      }),
    );
  }

  private readLinkedFile(link: { url: string; fileName: string }): void {
    const generation = this.readGeneration;
    this.draftText.set('');
    this.holdMessage.set('');
    this.readState.set('reading');
    this.subscriptionList.add(
      this.extractService.readFile({ url: link.url }).subscribe({
        next: (result) => {
          if (generation !== this.readGeneration) {
            return;
          }
          this.finishSuppliedFile(result, { fileName: link.fileName, documentUrl: result.fileUrl });
        },
        error: () => {
          if (generation !== this.readGeneration) {
            return;
          }
          this.readState.set('idle');
          this.holdMessage.set('This file could not be read.');
        },
      }),
    );
  }

  private readGoogleDoc(url: string): void {
    const generation = this.readGeneration;
    this.draftText.set('');
    this.holdMessage.set('');
    this.readState.set('reading');
    this.subscriptionList.add(
      this.extractService.readGoogleDoc({ url }).subscribe({
        next: (result) => {
          if (generation !== this.readGeneration) {
            return;
          }
          this.finishSuppliedGoogleDoc(result, url);
        },
        error: () => {
          if (generation !== this.readGeneration) {
            return;
          }
          this.readState.set('idle');
          this.keepFailedGoogleDoc(url);
          this.holdMessage.set('This Google Doc could not be opened. Share it so anyone with the link can view it.');
        },
      }),
    );
  }

  private finishSuppliedGoogleDoc(result: GoogleDocTextResponse, fallbackUrl: string): void {
    const documentUrl = result.documentUrl || fallbackUrl;
    if (result.outcome !== 'text' || result.text.trim().length === 0) {
      this.readState.set('idle');
      this.keepFailedGoogleDoc(documentUrl);
      this.holdMessage.set(
        result.outcome === 'empty'
          ? 'There is no text to read.'
          : 'This Google Doc could not be opened. Share it so anyone with the link can view it.',
      );
      this.draftText.set('');
      return;
    }
    const submitted = submitSuppliedGoogleDoc(this.conversation(), {
      text: result.text,
      title: result.title,
      documentUrl,
    });
    this.conversation.set(submitted.conversation);
    this.sentText.set(result.text);
    this.draftText.set('');
    this.holdMessage.set('');
    if (submitted.read) {
      this.readFact(submitted.read.text, submitted.read.briefType);
    }
  }

  private keepFailedGoogleDoc(documentUrl: string): void {
    const kept = submitSuppliedGoogleDoc(this.conversation(), { text: '', title: '', documentUrl });
    this.conversation.set(kept.conversation);
  }

  private finishSuppliedFile(
    result: BriefFileTextResponse,
    fallback: { fileName: string; documentUrl: string },
  ): void {
    const fileName = result.fileName || fallback.fileName;
    const documentUrl = result.fileUrl || fallback.documentUrl;
    if (result.outcome !== 'text' || result.text.trim().length === 0) {
      this.readState.set('idle');
      this.keepFailedFile(fileName, documentUrl);
      this.holdMessage.set(result.outcome === 'empty' ? 'There is no text to read.' : 'This file could not be read.');
      this.draftText.set('');
      return;
    }
    const submitted = submitSuppliedFile(this.conversation(), { text: result.text, fileName, documentUrl });
    this.conversation.set(submitted.conversation);
    this.sentText.set(result.text);
    this.draftText.set('');
    this.holdMessage.set('');
    if (submitted.read) {
      this.readFact(submitted.read.text, submitted.read.briefType);
    }
  }

  private keepFailedFile(fileName: string, documentUrl: string): void {
    const kept = submitSuppliedFile(this.conversation(), { text: '', fileName, documentUrl });
    this.conversation.set(kept.conversation);
  }

  private readFact(text: string, briefType: BriefType): void {
    this.readState.set('reading');
    const generation = this.readGeneration;
    this.subscriptionList.add(
      this.extractService.extract({ text, briefType }).subscribe({
        next: (read) => {
          if (generation !== this.readGeneration) {
            return;
          }
          this.readState.set('read');
          const applied = applyFactRead(this.conversation(), read);
          this.conversation.set(this.settleTypeQuestions(applied));
        },
        error: () => {
          if (generation !== this.readGeneration) {
            return;
          }
          this.readState.set('error');
          this.readError.set('The brief could not be read.');
          this.conversation.set(this.settleTypeQuestions(this.conversation()));
        },
      }),
    );
  }

  revisitPieces(): void {
    const current = this.conversation();
    if (current.draft.briefType !== 'ugc' || !current.draft.productId) {
      return;
    }
    this.seedPieceEditor(current.draft.pieces);
    this.conversation.set({ ...current, step: 'pieces' });
  }

  revisitProduct(): void {
    this.productCreate.set(false);
    this.createBriefService.selectedProduct.set(undefined);
    this.conversation.update((current) => reaskProduct(current));
  }

  revisitNewProduct(): void {
    this.productCreate.set(true);
    this.clearChosenProduct();
  }

  closeProductCreate(): void {
    this.productCreate.set(false);
  }

  private clearChosenProduct(): void {
    this.createBriefService.selectedProduct.set(undefined);
    this.conversation.update((current) => ({
      ...current,
      step: 'product',
      draft: {
        ...current.draft,
        productId: null,
        productName: null,
        productImage: null,
        productDescription: null,
        productValue: null,
        productCurrency: null,
      },
    }));
  }

  onProduct(product: UgcProductResponseDto): void {
    if (!this.productSeeded) {
      this.classicProduct = this.createBriefService.selectedProduct();
      this.productSeeded = true;
    }
    this.productCreate.set(false);
    this.createBriefService.selectedProduct.set(product);
    const chosen = chooseProduct(this.conversation(), {
      id: product.id,
      name: product.productName,
      image: product.productImage,
      description: product.productDescription,
      value: product.productValue ?? null,
      currency: product.currency ?? null,
    });
    this.conversation.set(this.settleTypeQuestions(chosen));
  }

  lookbackLabel(): string {
    return liveBriefValue(this.conversation().draft, 'lookback');
  }

  pickReport(reportType: 'standard' | 'custom'): void {
    this.conversation.update((current) => askForReview(chooseReport(current, reportType)));
    this.draftText.set('');
    this.holdMessage.set('');
  }

  confirmCurrentPieces(): void {
    const pieces = this.createBriefService.pieces() ?? [];
    const next = confirmPieces(this.conversation(), pieces);
    if (next.step === 'pieces') {
      this.holdMessage.set('Add at least one piece.');
      return;
    }
    this.holdMessage.set('');
    if (next.step === 'audience') {
      // The brief already gave a creator count, so the count question is skipped.
      this.prepareAudience(next);
    }
    this.conversation.set(next);
  }

  pickCreatorCount(count: number): void {
    const next = chooseCreatorCount(this.conversation(), count);
    if (next.step === 'audience') {
      this.prepareAudience(next);
    }
    this.conversation.set(next);
    this.draftText.set('');
    this.holdMessage.set('');
  }

  confirmCurrentAudience(): void {
    this.conversation.update((current) => askForReview(confirmAudience(current, this.audienceFromService())));
  }

  acceptBrief(): void {
    this.conversation.update((current) => looksGood(current));
    this.holdMessage.set('');
  }

  createActionLabel(): string {
    const briefType = this.conversation().draft.briefType;
    if (this.creating()) {
      return briefType === 'ugc' ? 'Publishing' : 'Creating';
    }
    if (briefType === 'ugc') {
      return 'Publish brief';
    }
    if (briefType === 'screening') {
      return 'Create screening brief';
    }
    if (briefType === 'monitoring') {
      return 'Create monitoring brief';
    }
    return 'Create';
  }

  createBrief(): void {
    const conversation = this.conversation();
    if (!this.createReady()) {
      return;
    }
    if (conversation.draft.briefType === 'ugc') {
      this.publishUgc(conversation.draft);
      return;
    }
    if (conversation.draft.briefType === 'screening') {
      this.createScreeningBrief(conversation.draft);
      return;
    }
    if (conversation.draft.briefType === 'monitoring') {
      this.createMonitoringBrief(conversation.draft);
    }
  }

  private applyRequestedBriefType(): void {
    const type = this.route.snapshot.queryParamMap.get('type');
    if (type === 'ugc' || type === 'screening' || type === 'monitoring') {
      this.onBriefType(type);
    }
  }

  onBriefType(briefType: BriefType): void {
    if (briefType === 'ugc') {
      this.rememberUgcHost();
      if (this.shoutAgency()) {
        this.createBriefService.selectedPricingModel.set(undefined);
      }
    }
    this.conversation.update((current) => chooseBriefType(current, briefType));
    this.followUpReady.set(false);
    if (this.followUpTimer !== null) {
      clearTimeout(this.followUpTimer);
    }
    this.followUpTimer = setTimeout(() => {
      this.followUpTimer = null;
      this.followUpReady.set(true);
    }, 650);
    this.briefComposer()?.nativeElement.focus();
  }

  startOver(): void {
    this.acceptDraftSaves = false;
    this.restoreClassicDraft();
    this.conversation.set(resetConversation(this.conversation()));
    this.draftSaved.set(false);
    this.savedCampaignId.set(null);
    this.savedPricingModelId.set(null);
    this.initFlow();
    this.beginGreeting();
    this.abandonDraft();
  }

  private sendCopy(step: 'dos' | 'donts'): void {
    const current = this.conversation();
    const next = step === 'dos'
      ? answerMandatoryItems(current, this.draftText())
      : answerRestrictions(current, this.draftText());
    if (next === current) {
      return;
    }
    this.draftText.set('');
    this.holdMessage.set('');
    this.conversation.set(this.settleTypeQuestions(next));
  }

  private sendCreatorCount(): void {
    const text = this.draftText().trim();
    const count = Number(text);
    if (!/^\d+$/.test(text) || !Number.isInteger(count) || count < 1) {
      this.holdMessage.set('Type a number of creators.');
      return;
    }
    this.pickCreatorCount(count);
  }

  private sendReportPrompt(): void {
    const next = setReportPrompt(this.conversation(), this.draftText());
    if (next.draft.reportPrompt.length === 0) {
      return;
    }
    this.conversation.set(askForReview(next));
    this.draftText.set('');
    this.holdMessage.set('');
  }

  private sendReviewChange(): void {
    const result = describeChange(this.conversation(), this.draftText());
    if (!result.read) {
      this.holdMessage.set('Describe the change.');
      return;
    }
    this.sentText.set(result.read.instruction);
    this.draftText.set('');
    this.holdMessage.set('');
    this.readState.set('reading');
    const generation = this.readGeneration;
    this.subscriptionList.add(
      this.extractService.extract({
        instruction: result.read.instruction,
        current: result.read.current,
        briefType: result.read.briefType,
      }).subscribe({
        next: (read) => {
          if (generation !== this.readGeneration) {
            return;
          }
          this.readState.set('read');
          this.conversation.set(applyFactRead(this.conversation(), read));
        },
        error: () => {
          if (generation !== this.readGeneration) {
            return;
          }
          this.readState.set('error');
          this.readError.set('The brief could not be read.');
        },
      }),
    );
  }

  private settleTypeQuestions(conversation: SmartBriefConversation): SmartBriefConversation {
    if (this.readState() !== 'read' && this.readState() !== 'error') {
      return conversation;
    }
    const withCopy = askForMissingCopy(conversation);
    if (withCopy.step === 'dos' || withCopy.step === 'donts') {
      return withCopy;
    }
    const next = askForPieces(withCopy);
    if (next.step === 'pieces' && conversation.step !== 'pieces') {
      this.seedPieceEditor(next.draft.pieces);
    }
    const screened = askForScreening(next);
    if (screened.step === 'lookback' && conversation.step !== 'lookback') {
      this.prepareScreeningWindow();
    }
    const watching = askForMonitoring(screened);
    if (watching.step === 'window' && conversation.step !== 'window') {
      this.prepareMonitoringWindow();
    }
    return watching;
  }

  confirmMonitoringRange(range: MonitoringRange): void {
    const next = setMonitoringWindow(
      this.conversation(),
      toIsoDate(range.start),
      toIsoDate(range.end),
    );
    if (!next.draft.monitorStart || !next.draft.monitorEnd) {
      this.holdMessage.set('Choose a start up to 30 days back and an end within a year.');
      return;
    }
    this.holdMessage.set('');
    this.conversation.set(askForReview(next));
  }

  windowLabel(): string {
    const start = liveBriefValue(this.conversation().draft, 'start');
    const end = liveBriefValue(this.conversation().draft, 'end');
    if (!start || !end) {
      return '';
    }
    return `${start} to ${end}`;
  }

  private prepareMonitoringWindow(): void {
    if (this.monitoringWindowSeeded) {
      return;
    }
    this.classicMonitorStart = this.createMonitoringService.monitorStart();
    this.classicMonitorEnd = this.createMonitoringService.monitorUntil();
    this.createMonitoringService.monitorStart.set(undefined);
    this.createMonitoringService.monitorUntil.set(undefined);
    this.monitoringWindowSeeded = true;
  }

  confirmScreeningRange(from: Date): void {
    const month = String(from.getMonth() + 1).padStart(2, '0');
    const day = String(from.getDate()).padStart(2, '0');
    const next = setLookback(this.conversation(), `${from.getFullYear()}-${month}-${day}`);
    if (next.step === 'lookback') {
      this.holdMessage.set('Choose a date up to today.');
      return;
    }
    this.holdMessage.set('');
    this.conversation.set(next);
  }

  private prepareScreeningWindow(): void {
    if (this.screeningWindowSeeded) {
      return;
    }
    this.classicScreenUntil = this.createScreeningService.screenContentUntil();
    this.createScreeningService.screenContentUntil.set(undefined);
    this.screeningWindowSeeded = true;
  }

  private seedPieceEditor(pieces: Piece[]): void {
    if (!this.pieceEditorSeeded) {
      this.classicPieces = this.createBriefService.pieces();
      this.classicEditor = this.createBriefService.pieceEditor();
      this.pieceEditorSeeded = true;
    }
    const editor = pieces.length > 0 ? editorFromSavedPieces(pieces) : createDefaultPieceEditor();
    this.createBriefService.pieceEditor.set(editor);
    this.createBriefService.pieces.set(piecesFromEditor(editor));
  }

  private prepareAudience(conversation: SmartBriefConversation): void {
    const posted = conversation.draft.pieces.some((item) =>
      item.placements.some((placement) => placement.kind === 'social'),
    );
    if (!this.classicAudience) {
      const persona = this.createBriefService.creatorProfileModel();
      this.classicAudience = {
        genders: [...this.createBriefService.selectedGenders()],
        interests: [...this.createBriefService.selectedInterests()],
        location: [...this.createBriefService.selectedLocation()],
        age: this.createBriefService.selectedAge().map((age) => ({ ...age })),
        countries: [...this.createBriefService.selectedCountryCodes()],
        is18plus: this.createBriefService.is18plus(),
        followerRanges: this.createBriefService.selectedFollowerRanges().map((range) => ({ ...range })),
        ugcOnly: this.createBriefService.selectedUgcOnly(),
        persona: persona ? { ...persona } : undefined,
      };
    }
    // Start from what the brief said about its creators (age, country, gender), not blank.
    const read = conversation.draft.extractedAudience;
    this.createBriefService.selectedGenders.set(
      (read?.genders ?? [])
        .map((name) => this.briefService.creatorGenders.find((gender) => gender.name === name)?.id)
        .filter((id): id is number => id !== undefined),
    );
    this.createBriefService.selectedInterests.set([]);
    this.createBriefService.selectedLocation.set([]);
    this.createBriefService.selectedAge.set(
      ageBracketsFor(this.briefService.creatorAges, read?.minAge ?? null, read?.maxAge ?? null).map((age) => ({ ...age })),
    );
    this.createBriefService.selectedCountryCodes.set(
      (read?.countryCodes ?? []).filter((code) =>
        this.briefService.countryCodes.some((country) => country.countryCode === code),
      ),
    );
    this.createBriefService.is18plus.set(read?.is18plus === true || (read?.minAge ?? 0) >= 18);
    this.createBriefService.selectedFollowerRanges.set([]);
    this.createBriefService.creatorProfileModel.set(undefined);
    this.createBriefService.selectedUgcOnly.set(!posted);
  }

  private audienceFromService(): SmartBriefAudience {
    const genders = this.createBriefService.selectedGenders()
      .map((id) => this.briefService.creatorGenders.find((gender) => gender.id === id)?.name ?? '')
      .filter((name) => name.length > 0);
    const interests = this.createBriefService.selectedInterests()
      .map((id) => this.briefService.creatorInterests.find((interest) => Number(interest.id) === id)?.name ?? '')
      .filter((name) => name.length > 0);
    const followerRanges = this.createBriefService.selectedFollowerRanges()
      .map((range) => FOLLOWER_RANGES.find((option) => option.min === range.min && option.max === range.max)?.label ?? '')
      .filter((label) => label.length > 0);
    const persona = this.createBriefService.creatorProfileModel();
    const personaText = [persona?.creator_persona, persona?.creator_audience, persona?.creator_content_style]
      .map((part) => part?.trim() ?? '')
      .filter((part) => part.length > 0)
      .join(' ');
    return {
      genders: genders.length > 0 ? genders : ['Any'],
      ages: this.createBriefService.selectedAge().map((age) => age.label),
      interests,
      locations: [...this.createBriefService.selectedLocation()],
      countries: this.createBriefService.selectedCountryCodes().map((code) =>
        this.briefService.countryCodes.find((country) => country.countryCode === code)?.countryName ?? code,
      ),
      is18plus: this.createBriefService.is18plus(),
      followerRanges,
      persona: personaText,
    };
  }

  private restoreClassicDraft(): void {
    if (this.pieceEditorSeeded) {
      this.createBriefService.pieces.set(this.classicPieces);
      this.createBriefService.pieceEditor.set(this.classicEditor);
      this.pieceEditorSeeded = false;
    }
    if (this.screeningWindowSeeded) {
      this.createScreeningService.screenContentUntil.set(this.classicScreenUntil);
      this.screeningWindowSeeded = false;
    }
    if (this.monitoringWindowSeeded) {
      this.createMonitoringService.monitorStart.set(this.classicMonitorStart);
      this.createMonitoringService.monitorUntil.set(this.classicMonitorEnd);
      this.monitoringWindowSeeded = false;
    }
    if (this.ugcHostSeeded) {
      this.createBriefService.selectedCampaign.set(this.classicCampaign);
      this.createBriefService.selectedPricingModel.set(this.classicModel);
      this.createBriefService.basicInfoModel.set({ briefTitle: this.classicTitle });
      this.createBriefService.isPublic.set(this.classicPublic);
      this.ugcHostSeeded = false;
    }
    if (this.productSeeded) {
      this.createBriefService.selectedProduct.set(this.classicProduct);
      this.productSeeded = false;
    }
    if (this.classicAudience) {
      this.createBriefService.selectedGenders.set(this.classicAudience.genders);
      this.createBriefService.selectedInterests.set(this.classicAudience.interests);
      this.createBriefService.selectedLocation.set(this.classicAudience.location);
      this.createBriefService.selectedAge.set(this.classicAudience.age);
      this.createBriefService.selectedCountryCodes.set(this.classicAudience.countries);
      this.createBriefService.is18plus.set(this.classicAudience.is18plus);
      this.createBriefService.selectedFollowerRanges.set(this.classicAudience.followerRanges);
      this.createBriefService.selectedUgcOnly.set(this.classicAudience.ugcOnly);
      this.createBriefService.creatorProfileModel.set(this.classicAudience.persona);
      this.classicAudience = null;
    }
  }

  exit(): void {
    this.location.back();
  }

  private shoutAgency(): boolean {
    return this.currentUser.focussedAgency?.agency.id === environment.shoutAgencyId;
  }

  private rememberUgcHost(): void {
    if (this.ugcHostSeeded) {
      return;
    }
    this.classicCampaign = this.createBriefService.selectedCampaign();
    this.classicModel = this.createBriefService.selectedPricingModel();
    this.classicTitle = this.createBriefService.basicInfoModel().briefTitle;
    this.classicPublic = this.createBriefService.isPublic();
    this.ugcHostSeeded = true;
  }

  private applyUgcDraft(draft: SmartBriefDraft): void {
    this.createBriefService.basicInfoModel.set({ briefTitle: draft.title });
    this.createBriefService.uploadedBriefDocumentUrl.set(draft.documentUrl ?? '');
    this.createBriefService.stepsHtml.set(draft.steps);
    this.createBriefService.doHtml.set(draft.dos);
    this.createBriefService.dontHtml.set(draft.donts);
    this.createBriefService.pieces.set(draft.pieces);
    this.createBriefService.requiredCreators.set(draft.creatorCount && draft.creatorCount > 0 ? draft.creatorCount : 1);
    this.createBriefService.videosPerCreator.set(Math.min(4, Math.max(1, draft.pieces.length)));
    this.createBriefService.requiresPieceList.set(true);
    this.createBriefService.pricingUserType.set('regular');
    this.createBriefService.isPublic.set(true);
    if (!draft.audience) {
      this.createBriefService.selectedGenders.set([]);
      this.createBriefService.selectedInterests.set([]);
      this.createBriefService.selectedLocation.set([]);
      this.createBriefService.selectedAge.set([]);
      this.createBriefService.selectedCountryCodes.set([]);
      this.createBriefService.is18plus.set(false);
      this.createBriefService.selectedFollowerRanges.set([]);
      this.createBriefService.creatorProfileModel.set(undefined);
    }
  }

  private publishUgc(draft: SmartBriefDraft): void {
    this.applyUgcDraft(draft);
    this.creating.set(true);
    this.holdMessage.set('');
    this.subscriptionList.add(
      this.createBriefService.sendFinalBrief().subscribe({
        next: (brief) => {
          this.creating.set(false);
          this.confirmCreated(['/manage-brief', brief.id, 2]);
        },
        error: () => {
          this.creating.set(false);
          this.holdMessage.set('Could not create the brief.');
        },
      }),
    );
  }

  private createScreeningBrief(draft: SmartBriefDraft): void {
    const businessAccountId = this.currentUser.focussedBrand?.id as string | undefined;
    if (!businessAccountId || !draft.productId) {
      this.holdMessage.set('Choose a product before creating the brief.');
      return;
    }
    const brief: CreateSocialScreeningBriefDto = {
      briefTitle: draft.title,
      businessAccountId,
      contentRequirements: plainTextParagraph(draft.requirements),
      contentDonts: plainTextParagraph(draft.restrictions),
      briefUrl: draft.documentUrl || undefined,
      productId: draft.productId,
      screenUntil: screenUntilFromLookback(draft.lookback),
      reportType: draft.reportType ?? 'standard',
      reportPrompt: draft.reportType === 'custom' ? draft.reportPrompt.trim() || undefined : undefined,
    };
    this.creating.set(true);
    this.holdMessage.set('');
    this.subscriptionList.add(
      this.socialScreening.createSocialScreeningBrief(brief).subscribe({
        next: (created) => {
          this.creating.set(false);
          this.confirmCreated(['/manage-brief-screen', created.id]);
        },
        error: () => {
          this.creating.set(false);
          this.holdMessage.set('Could not create the screening brief.');
        },
      }),
    );
  }

  private createMonitoringBrief(draft: SmartBriefDraft): void {
    const businessAccountId = this.currentUser.focussedBrand?.id as string | undefined;
    if (!businessAccountId || !draft.productId) {
      this.holdMessage.set('Choose a product before creating the brief.');
      return;
    }
    const dates = monitoringWindowFromDraft(draft.monitorStart, draft.monitorEnd);
    const brief: CreateMonitoringBriefDto = {
      briefTitle: draft.title,
      businessAccountId,
      contentRequirements: plainTextParagraph(draft.requirements),
      contentDonts: plainTextParagraph(draft.restrictions),
      briefUrl: draft.documentUrl || undefined,
      productId: draft.productId,
      monitorStart: dates.start,
      monitorUntil: dates.end,
      monitorType: MonitorTypeEnum.CREATOR_BRIEF_CHECK,
    };
    this.creating.set(true);
    this.holdMessage.set('');
    this.subscriptionList.add(
      this.socialMonitoring.createMonitoringBrief(brief).subscribe({
        next: (created) => {
          this.creating.set(false);
          const id = monitoringBriefId(created);
          if (!id) {
            this.holdMessage.set('Could not create the monitoring brief.');
            return;
          }
          this.confirmCreated(['/manage-brief-monitor', id]);
        },
        error: () => {
          this.creating.set(false);
          this.holdMessage.set('Could not create the monitoring brief.');
        },
      }),
    );
  }

  private confirmCreated(commands: (string | number)[]): void {
    this.acceptDraftSaves = false;
    this.draftSaved.set(false);
    this.completeDraft(String(commands[1] ?? ''));
    void this.alertService
      .confirm({
        title: 'Brief created successfully!',
        message: 'You can now view your brief in the briefs page.',
        confirmText: 'Manage Brief',
        cancelText: 'Back to Briefs',
        type: 'confirm',
        icon: 'success',
      })
      .then((result) => {
        if (result) {
          void this.router.navigate(commands);
          return;
        }
        this.location.back();
      });
  }

}

function documentKeyFromUrl(fullUrl: string): string {
  try {
    return decodeURIComponent(new URL(fullUrl).pathname.replace(/^\//, ''));
  } catch {
    return '';
  }
}

function monitoringWindowFromDraft(startIso: string | null, endIso: string | null): { start: Date; end: Date } {
  const start = fromIsoDate(startIso ?? '');
  const end = fromIsoDate(endIso ?? '');
  const today = new Date();
  if (start && end && isValidMonitoringStart(start, today) && isValidMonitoringEnd(start, end)) {
    return { start, end };
  }
  const fallbackStart = new Date();
  fallbackStart.setDate(fallbackStart.getDate() - 30);
  const fallbackEnd = new Date();
  fallbackEnd.setDate(fallbackEnd.getDate() + 30);
  return { start: fallbackStart, end: fallbackEnd };
}

function monitoringBriefId(response: unknown): string | undefined {
  if (typeof response !== 'object' || response === null || !('id' in response)) {
    return undefined;
  }
  const id = (response as { id: unknown }).id;
  return typeof id === 'string' ? id : undefined;
}

function screenUntilFromLookback(lookback: string | null): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(lookback ?? '');
  if (match) {
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (isPastCalendarDate(date)) {
      return toScreenUntilInstant(date);
    }
  }
  const fallback = new Date();
  fallback.setDate(fallback.getDate() - 30);
  return toScreenUntilInstant(fallback);
}

function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}
